/**
 * The boxes of a directional light that draws Godot's parallel splits, fitted to the viewing
 * camera split by split as `_light_instance_setup_directional_shadow` fits them
 * (`renderer_scene_cull.cpp:2168-2353`), and the per-split values the shader reads. Pure: it reads
 * THREE maths and writes nothing.
 */
import { blendsSplits, directionalShadowBlendStart, directionalShadowFade, directionalShadowSplitDistances, directionalShadowSplitEnds, directionalShadowSplitRange, directionalShadowSplitTextureSize, } from '../../godot/rendering/lights/directionalShadow.js';
import { directionalShadowBoxFitter, viewSlice, } from './fitDirectionalShadowBox.js';
/**
 * The blend start of a slot that does not blend into the next split. A real blend start is negative
 * for a negative split offset, which Godot's setter keeps, so the marker sits far below any depth.
 */
export const NO_BLEND = -1e30;
/** Null when a split gets no finite box. The caller then leaves the light's shadow as it is. */
export function fitDirectionalShadowSplits(input) {
    const { declaration } = input;
    const { splitCount } = declaration;
    const blends = blendsSplits(splitCount, declaration.blendSplits);
    const distances = directionalShadowSplitDistances(viewSlice(input), splitCount, declaration.splitOffsets);
    const fitSplit = directionalShadowBoxFitter({
        ...input,
        shadowMapSize: directionalShadowSplitTextureSize(splitCount, input.lightRect),
    });
    const boxes = [];
    for (let split = 0; split < splitCount; split++) {
        const box = fitSplit(directionalShadowSplitRange(distances, split, blends));
        if (!box)
            return null;
        boxes.push(box);
    }
    const lastSplit = splitCount - 1;
    const slots = directionalShadowSplitEnds(distances, splitCount).map((splitEnd, slot) => {
        const box = boxes[Math.min(slot, lastSplit)];
        const blendStart = blends && slot < lastSplit ? directionalShadowBlendStart(splitEnd) : NO_BLEND;
        return [splitEnd, box.bias, box.normalBias, blendStart];
    });
    const rangeBegins = slots.map((_, slot) => rangeBegin(boxes[Math.min(slot, lastSplit)]));
    return {
        boxes,
        fade: directionalShadowFade(distances[splitCount], declaration.fadeStart),
        slots,
        rangeBegins,
    };
}
function rangeBegin(box) {
    return box.rangeBegin + box.bias * (box.far - box.near);
}
//# sourceMappingURL=fitDirectionalShadowSplits.js.map